import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { apiFetch, getIdToken, listResource, deleteResource } from '@diggens/api-client';

interface DataExportRequest {
  id: string;
  tenant_id: string;
  user_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  file_path: string | null;
  file_url: string | null;
  file_size: number | null;
  expires_at: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}
import { useSignOut } from '@diggens/shared-hooks';
import { useCurrentUser } from '@diggens/user-management';

export function DataPrivacy() {
  const { data: user } = useCurrentUser();
  const signOut = useSignOut();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    submissions: 0,
    metrics: 0,
    files: 0,
    storageBytes: 0,
  });
  const [retentionPolicy, setRetentionPolicy] = useState('delete_immediately');
  const [saving, setSaving] = useState(false);
  const [accountDeleteOpen, setAccountDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [exportStatus, setExportStatus] = useState<'idle' | 'requesting' | 'processing' | 'ready'>('idle');
  const [currentExport, setCurrentExport] = useState<DataExportRequest | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [submissionsResult, metricsResult, filesStats] = await Promise.all([
          listResource('submissions', { limit: 0 }),
          listResource('metrics', { limit: 0 }),
          Promise.resolve({ count: 0, totalBytes: 0 }),
        ]);
        const submissionsCount = submissionsResult.count;
        const metricsCount = metricsResult.count;

        setStats({
          submissions: submissionsCount,
          metrics: metricsCount,
          files: filesStats.count,
          storageBytes: filesStats.totalBytes,
        });
      } catch (error) {
        console.error('Error fetching data stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDownloadData = async () => {
    try {
      setExportStatus('processing');

      // Call export function directly - this waits until complete
      const token = await getIdToken();

      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/files/generate-data-export`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Export failed');
      }

      const result = await response.json();

      // Show download button immediately
      setCurrentExport({
        id: 'direct',
        file_url: result.downloadUrl,
        file_size: result.fileSize,
        expires_at: result.expiresAt,
        status: 'completed',
        tenant_id: user?.tenant_id || '',
        user_id: user?.id || '',
        created_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
        file_path: null,
        error_message: null,
      });
      setExportStatus('ready');
    } catch (error) {
      console.error('Error generating export:', error);
      setExportStatus('idle');
      alert(error instanceof Error ? error.message : 'Failed to generate data export');
    }
  };

  const handleSaveRetentionPolicy = async () => {
    if (!user?.tenant_id) return;

    try {
      setSaving(true);
      await apiFetch('/settings/retention-policy', {
        method: 'PUT',
        body: JSON.stringify({ retention_policy: retentionPolicy }),
      });
      alert('Retention policy updated successfully');
    } catch (error) {
      console.error('Error updating retention policy:', error);
      alert('Failed to update retention policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAllData = async () => {
    if (!confirm('Are you sure you want to delete ALL data? This cannot be undone.')) {
      return;
    }

    try {
      // Delete all submissions in pages (resource handler caps at 1000)
      let hasMore = true;
      while (hasMore) {
        const page = await listResource<{ id: string }>('submissions', { limit: 1000, select: 'id' });
        if (page.data.length === 0) {
          hasMore = false;
          break;
        }
        for (const sub of page.data) {
          await deleteResource('submissions', sub.id);
        }
      }

      alert('All data deleted successfully');
      // Refresh stats
      window.location.reload();
    } catch (error) {
      console.error('Error deleting data:', error);
      alert('Failed to delete data');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      alert('Please type DELETE to confirm');
      return;
    }

    if (!user?.tenant_id) return;

    try {
      // Mark tenant as deleted
      await apiFetch('/settings/delete-account', { method: 'POST' });

      // Log out
      await signOut.mutateAsync();
      window.location.href = '/login';
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Data Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your Data Summary
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Box>
                <Typography variant="h3" color="primary">
                  {stats.submissions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Submissions
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Box>
                <Typography variant="h3" color="primary">
                  {stats.metrics}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Metrics Extracted
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Box>
                <Typography variant="h3" color="primary">
                  {stats.files}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Files Uploaded
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Box>
                <Typography variant="h3" color="primary">
                  {formatBytes(stats.storageBytes)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Storage Used
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            {exportStatus === 'ready' && currentExport?.file_url ? (
              <>
                <Button variant="contained" color="success" href={currentExport.file_url} download>
                  Download ZIP ({formatBytes(currentExport.file_size || 0)})
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                  Link expires: {new Date(currentExport.expires_at!).toLocaleString()}
                </Typography>
              </>
            ) : (
              <>
                <Button variant="contained" onClick={handleDownloadData} disabled={exportStatus !== 'idle'}>
                  {exportStatus === 'requesting' && 'Requesting...'}
                  {exportStatus === 'processing' && 'Processing... (this may take a few minutes)'}
                  {exportStatus === 'idle' && 'Download All Data'}
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                  Export all your submissions, metrics, and files as a ZIP file
                </Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Retention Policy */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Document Retention Policy
          </Typography>
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <FormLabel component="legend">How long should we keep your uploaded files?</FormLabel>
            <RadioGroup value={retentionPolicy} onChange={(e) => setRetentionPolicy(e.target.value)} sx={{ mt: 1 }}>
              <FormControlLabel value="delete_immediately" control={<Radio />} label="Delete immediately after extraction (most secure)" />
              <FormControlLabel value="keep_30_days" control={<Radio />} label="Keep for 30 days (allows re-extraction)" />
              <FormControlLabel value="keep_90_days" control={<Radio />} label="Keep for 90 days (allows quarterly comparison)" />
              <FormControlLabel value="keep_manual" control={<Radio />} label="Keep until I delete them manually" />
            </RadioGroup>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSaveRetentionPolicy} disabled={saving}>
              {saving ? 'Saving...' : 'Save Policy'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Data */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Delete Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently delete your data. This action cannot be undone.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Delete All Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Delete all submissions, metrics, and files for your entire organization
            </Typography>
            <Button variant="outlined" color="error" onClick={handleDeleteAllData}>
              Delete All Data
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Account Deletion */}
      <Card sx={{ mb: 3, borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="error">
            ⚠️ Delete Account & All Data
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will permanently delete your account and all associated data within 30 days. This action cannot be undone.
          </Typography>
          <Button variant="contained" color="error" onClick={() => setAccountDeleteOpen(true)}>
            Delete My Account
          </Button>
        </CardContent>
      </Card>

      {/* Account Deletion Dialog */}
      <Dialog open={accountDeleteOpen} onClose={() => setAccountDeleteOpen(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will permanently delete your account and all data. This action cannot be undone.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Type <strong>DELETE</strong> to confirm:
          </Typography>
          <TextField fullWidth value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="DELETE" />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAccountDeleteOpen(false);
              setDeleteConfirmText('');
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained" disabled={deleteConfirmText !== 'DELETE'}>
            Delete Account Forever
          </Button>
        </DialogActions>
      </Dialog>

      {/* Security Info */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Security & Privacy
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              🔒 <strong>Encryption:</strong> All data encrypted at rest (AES-256) and in transit (TLS 1.3)
            </Typography>
            <Typography variant="body2" gutterBottom>
              🤖 <strong>AI Processing:</strong> Zero-retention - your data is never used to train AI models
            </Typography>
            <Typography variant="body2" gutterBottom>
              ✓ <strong>Data Ownership:</strong> You own all your data and can export or delete it anytime
            </Typography>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Legal Documents
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="text" size="small" href={`${import.meta.env.VITE_MARKETING_URL}/legal/privacy`} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </Button>
              <Button variant="text" size="small" href={`${import.meta.env.VITE_MARKETING_URL}/legal/terms`} target="_blank" rel="noopener noreferrer">
                Terms of Service
              </Button>
              <Button variant="text" size="small" href={`${import.meta.env.VITE_MARKETING_URL}/legal/ai-policy`} target="_blank" rel="noopener noreferrer">
                AI Usage Policy
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
}
